var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable complexity */
/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 *
 */
import { AuthSdkError } from '../errors';
import { isInteractionRequiredError, urlParamsToObject } from './util';
import { isString } from '../util';
import { handleOAuthResponse } from './handleOAuthResponse';
function removeHash(sdk) {
    var nativeHistory = sdk.token.parseFromUrl._getHistory();
    var nativeDoc = sdk.token.parseFromUrl._getDocument();
    var nativeLoc = sdk.token.parseFromUrl._getLocation();
    if (nativeHistory && nativeHistory.replaceState) {
        nativeHistory.replaceState(null, nativeDoc.title, nativeLoc.pathname + nativeLoc.search);
    }
    else {
        nativeLoc.hash = '';
    }
}
function removeSearch(sdk) {
    var nativeHistory = sdk.token.parseFromUrl._getHistory();
    var nativeDoc = sdk.token.parseFromUrl._getDocument();
    var nativeLoc = sdk.token.parseFromUrl._getLocation();
    if (nativeHistory && nativeHistory.replaceState) {
        nativeHistory.replaceState(null, nativeDoc.title, nativeLoc.pathname + nativeLoc.hash);
    }
    else {
        nativeLoc.search = '';
    }
}
export function getResponseMode(sdk) {
    // https://openid.net/specs/openid-connect-core-1_0.html#Authentication
    var defaultResponseMode = sdk.options.pkce ? 'query' : 'fragment';
    var responseMode = sdk.options.responseMode || defaultResponseMode;
    return responseMode;
}
export function parseOAuthResponseFromUrl(sdk, options) {
    options = options || {};
    if (isString(options)) {
        options = { url: options };
    }
    else {
        options = options;
    }
    var url = options.url;
    var responseMode = options.responseMode || getResponseMode(sdk);
    var nativeLoc = sdk.token.parseFromUrl._getLocation();
    var paramStr;
    if (responseMode === 'query') {
        paramStr = url ? url.substring(url.indexOf('?')) : nativeLoc.search;
    }
    else {
        paramStr = url ? url.substring(url.indexOf('#')) : nativeLoc.hash;
    }
    if (!paramStr) {
        throw new AuthSdkError('Unable to parse a token from the url');
    }
    return urlParamsToObject(paramStr);
}
export function cleanOAuthResponseFromUrl(sdk, options) {
    // Clean hash or search from the url
    const responseMode = options.responseMode || getResponseMode(sdk);
    responseMode === 'query' ? removeSearch(sdk) : removeHash(sdk);
}
export function parseFromUrl(sdk, options) {
    return __awaiter(this, void 0, void 0, function* () {
        options = options || {};
        if (isString(options)) {
            options = { url: options };
        }
        else {
            options = options;
        }
        const res = parseOAuthResponseFromUrl(sdk, options);
        const state = res.state;
        const oauthParams = sdk.transactionManager.load({
            oauth: true,
            pkce: sdk.options.pkce,
            state
        });
        if (!oauthParams) {
            return Promise.reject(new AuthSdkError('Unable to retrieve OAuth redirect params from storage'));
        }
        const urls = oauthParams.urls;
        delete oauthParams.urls;
        if (!options.url) {
            // Clean hash or search from the url
            cleanOAuthResponseFromUrl(sdk, options);
        }
        return handleOAuthResponse(sdk, oauthParams, res, urls)
            .catch(err => {
            if (!isInteractionRequiredError(err)) {
                sdk.transactionManager.clear({
                    state
                });
            }
            throw err;
        })
            .then(res => {
            sdk.transactionManager.clear({
                state
            });
            return res;
        });
    });
}
